import React, { useState, useRef } from "react";
import { Pencil } from "lucide-react";
import { Section } from "../../../services/instantNewsletterService";

interface SectionReviewStepProps {
  sections: Section[];
  alternativeSections: Section[];
  contentWorkflow: string;
  onConfirm: (sections: Section[]) => void;
  isLoading: boolean;
}

const FORMAT_LABELS: Record<string, string> = {
  short_original: "Short Original",
  long_original: "Long Form",
  short_summary: "Short Summary",
  link_dump: "Link Roundup",
  testimonials: "Testimonials",
  featured_job: "Featured Job",
};

const CONTENT_TYPE_OPTIONS = [
  "Original Content",
  "News",
  "Community Content",
  "Jobs",
  "Events",
];

const FORMAT_OPTIONS = [
  "short_original",
  "long_original",
  "short_summary",
  "link_dump",
  "testimonials",
  "featured_job",
];

const SectionReviewStep: React.FC<SectionReviewStepProps> = ({
  sections: initialSections,
  alternativeSections: initialAlternatives,
  contentWorkflow,
  onConfirm,
  isLoading,
}) => {
  const [sections, setSections] = useState<Section[]>(initialSections);
  const [alternatives, setAlternatives] = useState<Section[]>(initialAlternatives || []);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragNode = useRef<HTMLDivElement | null>(null);

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDragIndex(index);
    dragNode.current = e.target as HTMLDivElement;
    e.dataTransfer.effectAllowed = "move";
    // Add a slight delay to show the dragging state
    setTimeout(() => {
      if (dragNode.current) dragNode.current.style.opacity = "0.4";
    }, 0);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragIndex === null || dragIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (dragNode.current) dragNode.current.style.opacity = "1";
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      setSections((prev) => {
        const updated = [...prev];
        const [moved] = updated.splice(dragIndex, 1);
        updated.splice(dragOverIndex, 0, moved);
        return updated;
      });
    }
    setDragIndex(null);
    setDragOverIndex(null);
    dragNode.current = null;
  };

  const handleFieldChange = (index: number, field: keyof Section, value: string) => {
    setSections((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    );
  };

  const handleRemove = (index: number) => {
    const removed = sections[index];
    setSections((prev) => prev.filter((_, i) => i !== index));
    // Move to alternatives
    setAlternatives((prev) => [...prev, removed]);
    if (editingIndex === index) setEditingIndex(null);
  };

  const handleAddAlternative = (altIndex: number) => {
    const added = alternatives[altIndex];
    setAlternatives((prev) => prev.filter((_, i) => i !== altIndex));
    setSections((prev) => [...prev, added]);
  };

  const handleConfirm = () => {
    onConfirm(sections);
  };

  const workflowLabel =
    contentWorkflow === "roundup"
      ? "Roundup — we curate everything, you review ~10 min/week"
      : contentWorkflow === "content_interview"
      ? "Content Interview — weekly call to extract your thinking"
      : "Hybrid — mix of curated content and your original pieces";

  return (
    <div style={{ maxWidth: "580px", width: "100%", textAlign: "left" }}>
      <h2 style={{ fontSize: "1.5rem", marginBottom: "0.25rem", textAlign: "center" }}>
        Your Newsletter Sections
      </h2>
      <p style={{ color: "#666", marginBottom: "1.5rem", textAlign: "center", fontSize: "0.9rem" }}>
        Drag to reorder, click to edit, or add alternatives below.
      </p>

      {/* Main sections - draggable */}
      <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "1.5rem" }}>
        {sections.map((section, i) => (
          <div
            key={`section-${i}-${section.title}`}
            draggable
            onDragStart={(e) => handleDragStart(e, i)}
            onDragOver={(e) => handleDragOver(e, i)}
            onDragEnd={handleDragEnd}
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "16px 20px",
              border: dragOverIndex === i ? "2px solid #000" : "1px solid #eee",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              cursor: "grab",
              transition: "border 0.15s ease",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              {/* Drag handle */}
              <div style={{ color: "#ccc", fontSize: "1.2rem", cursor: "grab", paddingTop: "2px", userSelect: "none", flexShrink: 0 }}>
                ⋮⋮
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                {editingIndex === i ? (
                  /* Editing mode */
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <input
                      type="text"
                      value={section.title}
                      onChange={(e) => handleFieldChange(i, "title", e.target.value)}
                      autoFocus
                      style={{
                        fontSize: "1rem",
                        fontWeight: 600,
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                        padding: "6px 8px",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    />
                    <textarea
                      value={section.what_it_includes}
                      onChange={(e) => handleFieldChange(i, "what_it_includes", e.target.value)}
                      rows={2}
                      style={{
                        fontSize: "0.85rem",
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                        padding: "6px 8px",
                        resize: "vertical",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    />
                    <div style={{ display: "flex", gap: "8px" }}>
                      <select
                        value={section.format}
                        onChange={(e) => handleFieldChange(i, "format", e.target.value)}
                        style={{
                          fontSize: "0.8rem",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          padding: "4px 8px",
                          flex: 1,
                        }}
                      >
                        {FORMAT_OPTIONS.map((f) => (
                          <option key={f} value={f}>{FORMAT_LABELS[f] || f}</option>
                        ))}
                      </select>
                      <select
                        value={section.content_type}
                        onChange={(e) => handleFieldChange(i, "content_type", e.target.value)}
                        style={{
                          fontSize: "0.8rem",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          padding: "4px 8px",
                          flex: 1,
                        }}
                      >
                        {CONTENT_TYPE_OPTIONS.map((ct) => (
                          <option key={ct} value={ct}>{ct}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={() => setEditingIndex(null)}
                      style={{
                        alignSelf: "flex-end",
                        background: "#000",
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px",
                        padding: "4px 12px",
                        fontSize: "0.8rem",
                        cursor: "pointer",
                      }}
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  /* Display mode */
                  <div onClick={() => setEditingIndex(i)} style={{ cursor: "pointer" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <h4 style={{ margin: 0, fontSize: "1rem" }}>{section.title}</h4>
                      <Pencil size={13} color="#bbb" style={{ flexShrink: 0 }} />
                    </div>
                    <p style={{ color: "#666", fontSize: "0.85rem", margin: "6px 0 4px" }}>
                      {section.what_it_includes}
                    </p>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      <span style={{
                        fontSize: "0.7rem",
                        background: "#f0f0f0",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        color: "#555",
                        fontWeight: 500,
                      }}>
                        {FORMAT_LABELS[section.format] || section.format}
                      </span>
                      <span style={{
                        fontSize: "0.7rem",
                        background: "#f0f0f0",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        color: "#555",
                        fontWeight: 500,
                      }}>
                        {section.content_type}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Remove button */}
              <button
                onClick={(e) => { e.stopPropagation(); handleRemove(i); }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#999",
                  fontSize: "1.2rem",
                  padding: "0 4px",
                  flexShrink: 0,
                }}
                title="Remove section"
              >
                x
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Alternative sections */}
      {alternatives.length > 0 && (
        <div style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ fontSize: "0.85rem", fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>
            Alternative Sections
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {alternatives.map((alt, i) => (
              <div
                key={`alt-${i}-${alt.title}`}
                style={{
                  background: "#fafafa",
                  borderRadius: "10px",
                  padding: "12px 16px",
                  border: "1px dashed #ddd",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h4 style={{ margin: 0, fontSize: "0.9rem", color: "#555" }}>{alt.title}</h4>
                  <p style={{ color: "#888", fontSize: "0.8rem", margin: "4px 0" }}>
                    {alt.what_it_includes}
                  </p>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <span style={{
                      fontSize: "0.65rem",
                      background: "#e8e8e8",
                      padding: "2px 6px",
                      borderRadius: "3px",
                      color: "#666",
                    }}>
                      {FORMAT_LABELS[alt.format] || alt.format}
                    </span>
                    <span style={{
                      fontSize: "0.65rem",
                      background: "#e8e8e8",
                      padding: "2px 6px",
                      borderRadius: "3px",
                      color: "#666",
                    }}>
                      {alt.content_type}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleAddAlternative(i)}
                  style={{
                    background: "#000",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "6px 14px",
                    fontSize: "0.8rem",
                    cursor: "pointer",
                    whiteSpace: "nowrap", maxWidth: "none",
                    flexShrink: 0,
                  }}
                >
                  + Add
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content workflow */}
      <div style={{ background: "#f5f5f5", borderRadius: "8px", padding: "12px 16px", marginBottom: "1.5rem" }}>
        <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#555" }}>Content Workflow: </span>
        <span style={{ fontSize: "0.8rem", color: "#666" }}>{workflowLabel}</span>
      </div>

      <button
        onClick={handleConfirm}
        disabled={sections.length < 1 || isLoading}
        className="secondary-button"
        style={{
          width: "100%",
          padding: "12px",
          fontSize: "0.95rem",
          whiteSpace: "nowrap", maxWidth: "none",
          opacity: sections.length < 1 || isLoading ? 0.5 : 1,
        }}
      >
        {isLoading ? "Generating strategy..." : "Confirm Sections →"}
      </button>
    </div>
  );
};

export default SectionReviewStep;
